import React, { useState, useEffect, useCallback } from 'react';
import { Building2, Calendar, Clock, CheckCircle, AlertTriangle, XCircle, Plus, RefreshCw, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TenantSubscription = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [extensionDays, setExtensionDays] = useState(30);
  const [isExtending, setIsExtending] = useState(false);

  // Redirect if not superadmin
  useEffect(() => {
    if (user && !['super_admin', 'superadmin'].includes(user.role?.toLowerCase())) {
      toast.error('Akses ditolak');
      navigate('/app/dashboard');
    }
  }, [user, navigate]);

  const fetchTenants = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    
    try {
      const response = await axios.get(`${API_URL}/api/v2/superadmin/tenants-subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setTenants(response.data.tenants || []);
      }
    } catch (error) {
      console.error('Error fetching tenants:', error);
      toast.error('Gagal memuat data tenant');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const handleExtendSubscription = async () => {
    if (!selectedTenant) return;
    
    setIsExtending(true);
    try {
      const response = await axios.post(`${API_URL}/api/v2/superadmin/extend-subscription`, {
        tenant_id: selectedTenant.tenant_id,
        days: extensionDays
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(`Langganan ${selectedTenant.company_name} berhasil diperpanjang ${extensionDays} hari`);
        setSelectedTenant(null);
        fetchTenants();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memperpanjang langganan');
    } finally {
      setIsExtending(false);
    }
  };

  const getStatusConfig = (status, daysRemaining) => {
    if (status === 'blocked') {
      return { color: 'bg-red-100 text-red-800 border-red-300', icon: XCircle, label: 'Diblokir' };
    } else if (status === 'expired' || daysRemaining <= 0) {
      return { color: 'bg-red-100 text-red-800 border-red-300', icon: XCircle, label: 'Expired' };
    } else if (daysRemaining <= 3) {
      return { color: 'bg-orange-100 text-orange-800 border-orange-300', icon: AlertTriangle, label: 'Segera Habis' };
    } else if (daysRemaining <= 7) {
      return { color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: AlertTriangle, label: 'Hampir Habis' };
    } else {
      return { color: 'bg-green-100 text-green-800 border-green-300', icon: CheckCircle, label: 'Aktif' };
    }
  };

  const filteredTenants = tenants.filter(t => 
    t.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.tenant_id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Count stats
  const stats = {
    total: tenants.length,
    active: tenants.filter(t => t.days_remaining > 7).length,
    warning: tenants.filter(t => t.days_remaining > 0 && t.days_remaining <= 7).length,
    expired: tenants.filter(t => t.days_remaining <= 0).length
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="px-6 py-6 space-y-6" data-testid="tenant-subscription-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2 dark:text-slate-200">
            <Building2 className="w-7 h-7 text-indigo-600 dark:text-indigo-400" />
            Perpanjangan Langganan Tenant
          </h1>
          <p className="text-sm text-slate-500 mt-1 dark:text-slate-400">
            Kelola dan perpanjang langganan tenant
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchTenants}
          disabled={isLoading}
          className="flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-md bg-gradient-to-br from-blue-50 to-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium dark:text-blue-400">Total Tenant</p>
                <p className="text-3xl font-bold text-blue-800">{stats.total}</p>
              </div>
              <Building2 className="w-10 h-10 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md bg-gradient-to-br from-green-50 to-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium dark:text-green-400">Aktif</p>
                <p className="text-3xl font-bold text-green-800">{stats.active}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md bg-gradient-to-br from-yellow-50 to-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-600 font-medium dark:text-yellow-400">Hampir Habis</p>
                <p className="text-3xl font-bold text-yellow-800">{stats.warning}</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-yellow-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md bg-gradient-to-br from-red-50 to-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600 font-medium dark:text-red-400">Expired</p>
                <p className="text-3xl font-bold text-red-800">{stats.expired}</p>
              </div>
              <XCircle className="w-10 h-10 text-red-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
          <Input
            placeholder="Cari tenant..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Tenant List */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="border-b border-slate-100 dark:border-slate-700">
          <CardTitle className="text-lg">Daftar Tenant</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto"></div>
            </div>
          ) : filteredTenants.length === 0 ? (
            <div className="text-center py-12 text-slate-500 dark:text-slate-400">
              <Building2 className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>Tidak ada tenant ditemukan</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase dark:text-slate-300">Tenant</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase dark:text-slate-300">ID</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase dark:text-slate-300">Mulai</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase dark:text-slate-300">Berakhir</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase dark:text-slate-300">Sisa Hari</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase dark:text-slate-300">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase dark:text-slate-300">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {filteredTenants.map((tenant) => {
                    const statusConfig = getStatusConfig(tenant.status, tenant.days_remaining);
                    const StatusIcon = statusConfig.icon;
                    
                    return (
                      <tr key={tenant.tenant_id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-semibold text-slate-800 dark:text-slate-200">{tenant.company_name}</p>
                            <p className="text-xs text-slate-500 dark:text-slate-400">{tenant.admin_email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <code className="text-xs bg-slate-100 px-2 py-1 rounded dark:bg-slate-800">{tenant.tenant_id}</code>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                          {tenant.start_date || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                          {tenant.end_date || '-'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`font-bold ${tenant.days_remaining <= 0 ? 'text-red-600' : tenant.days_remaining <= 7 ? 'text-yellow-600' : 'text-green-600'}`}>
                            {tenant.days_remaining} hari
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={`${statusConfig.color} flex items-center gap-1 w-fit`}>
                            <StatusIcon className="w-3 h-3" />
                            {statusConfig.label}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Button
                            size="sm"
                            onClick={() => setSelectedTenant(tenant)}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white"
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Perpanjang
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Extension Modal */}
      {selectedTenant && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader className="border-b">
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                Perpanjang Langganan
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg dark:bg-slate-900">
                <p className="font-semibold text-lg">{selectedTenant.company_name}</p>
                <p className="text-sm text-slate-600 dark:text-slate-300">ID: {selectedTenant.tenant_id}</p>
                <p className="text-sm text-slate-600 dark:text-slate-300">Berakhir: {selectedTenant.end_date}</p>
                <p className="text-sm text-slate-600 dark:text-slate-300">Sisa: <span className="font-bold">{selectedTenant.days_remaining} hari</span></p>
              </div>
              
              <div>
                <Label>Tambah Hari</Label>
                <div className="flex gap-2 mt-2">
                  {[7, 30, 90, 365].map((days) => (
                    <button
                      key={days}
                      onClick={() => setExtensionDays(days)}
                      className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all
                        ${extensionDays === days 
                          ? 'bg-indigo-600 text-white border-indigo-600' 
                          : 'bg-white text-slate-700 border-slate-300 hover:border-indigo-400'}`}
                    >
                      {days === 365 ? '1 Tahun' : `${days} Hari`}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <Label>Atau masukkan manual</Label>
                <Input
                  type="number"
                  min="1"
                  value={extensionDays}
                  onChange={(e) => setExtensionDays(parseInt(e.target.value) || 1)}
                  className="mt-1"
                />
              </div>
              
              <div className="bg-green-50 p-3 rounded-lg dark:bg-green-900/30">
                <p className="text-sm text-green-800">
                  <strong>Tanggal baru:</strong> {selectedTenant.end_date ? 
                    new Date(new Date(selectedTenant.end_date).getTime() + extensionDays * 24 * 60 * 60 * 1000).toLocaleDateString('id-ID') 
                    : '-'}
                </p>
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setSelectedTenant(null)}
                >
                  Batal
                </Button>
                <Button 
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                  onClick={handleExtendSubscription}
                  disabled={isExtending}
                >
                  {isExtending ? 'Memproses...' : `Perpanjang ${extensionDays} Hari`}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default TenantSubscription;
